import { findJobOutcomeWindow } from "./jobStatusComponent";

type OriginJob = { jobId: string; correlationJobIds?: string[]; workspaceSceneId?: unknown };
type LegacyScene = { id: string; jobIds: readonly string[] };

export function isWorkspaceSceneId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 256 && value.trim() === value;
}

/** The durable job field wins; catalog references retain pre-field origins. */
export function resolveJobCompletionScene(jobs: readonly OriginJob[], scenes: readonly LegacyScene[]): string | null {
  if (!jobs.length) throw new Error("Completion jobs are unavailable");
  const destinations = jobs.map((job) => {
    if (job.workspaceSceneId !== undefined) {
      if (!isWorkspaceSceneId(job.workspaceSceneId)) throw new Error("Invalid completion scene");
      return job.workspaceSceneId;
    }
    for (const jobId of [job.jobId, ...(job.correlationJobIds ?? [])]) {
      const references = scenes.filter(scene => scene.jobIds.includes(jobId));
      if (references.length > 1) throw new Error("Ambiguous completion scene");
      if (references.length === 1) return references[0].id;
    }
    return null;
  });
  if (new Set(destinations).size !== 1) throw new Error("Completion jobs belong to different scenes");
  return destinations[0];
}

/** A closed or ambiguous scene retains its outcome instead of choosing a window. */
export function findJobCompletionWindow(args: {
  windows: Record<string, readonly { id: string; name: string }[]>;
  selections: Record<string, { ownerId: string; sceneId: string | null }>;
  ownerId: string;
  sceneId: string | null;
  monitorId: string;
}): string | null {
  if (args.sceneId === null) return findJobOutcomeWindow(args.windows, args.monitorId);
  const matches = Object.entries(args.selections).filter(([windowId, selection]) =>
    selection.ownerId === args.ownerId && selection.sceneId === args.sceneId && Array.isArray(args.windows[windowId]));
  return matches.length === 1 ? matches[0][0] : null;
}
